import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Button, Input, Modal, Select, Spin, Switch, Upload, message } from 'antd';
import {
  ArrowLeftOutlined, BoldOutlined, ItalicOutlined, StrikethroughOutlined,
  OrderedListOutlined, UnorderedListOutlined, LinkOutlined, PictureOutlined,
  UndoOutlined, RedoOutlined, MinusOutlined, DeleteOutlined, InboxOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useEditor, EditorContent, Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { newsApi, NewsCreate } from '../../api/news';
import { NEWS_CATEGORIES } from '../../utils/constants';
import { L } from '../../styles/theme';
import { DistrictSelect } from '../../components/shared';

type Lang = 'ru' | 'kk';

const DRAFT_KEY = (id?: string) => `news_draft_${id || 'new'}`;

/* ─────────────── toolbar ─────────────── */

const ToolBtn: React.FC<{
  onClick: () => void;
  active?: boolean;
  title: string;
  children: React.ReactNode;
}> = ({ onClick, active, title, children }) => (
  <button
    type="button"
    title={title}
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    style={{
      minWidth: 26,
      height: 26,
      padding: '0 7px',
      borderRadius: 5,
      border: 'none',
      background: active ? L.accentBg : 'transparent',
      color: active ? L.accent : L.text,
      fontSize: 12,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      fontFamily: 'inherit',
    }}
  >
    {children}
  </button>
);

const ToolSep = () => (
  <span style={{ width: 1, height: 16, background: L.line, margin: '0 4px', display: 'inline-block' }} />
);

const MenuBar: React.FC<{ editor: Editor | null; onInsertImage: () => void }> = ({ editor, onInsertImage }) => {
  const [, force] = useState(0);

  useEffect(() => {
    if (!editor) return;
    const rerender = () => force((n) => n + 1);
    editor.on('transaction', rerender);
    editor.on('selectionUpdate', rerender);
    return () => {
      editor.off('transaction', rerender);
      editor.off('selectionUpdate', rerender);
    };
  }, [editor]);

  if (!editor) return null;

  const setLink = () => {
    const prev = editor.getAttributes('link').href as string | undefined;
    let value = prev || '';
    Modal.confirm({
      title: 'Ссылка',
      icon: null,
      content: (
        <Input
          defaultValue={prev}
          placeholder="https://…"
          onChange={(e) => { value = e.target.value; }}
          onPressEnter={() => {
            Modal.destroyAll();
            if (value) editor.chain().focus().extendMarkRange('link').setLink({ href: value }).run();
          }}
        />
      ),
      okText: 'Вставить',
      cancelText: prev ? 'Убрать ссылку' : 'Отмена',
      onOk: () => {
        if (value) editor.chain().focus().extendMarkRange('link').setLink({ href: value }).run();
      },
      onCancel: () => {
        if (prev) editor.chain().focus().extendMarkRange('link').unsetLink().run();
      },
    });
  };

  const words = editor.getText().trim().split(/\s+/).filter(Boolean).length;

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      padding: '5px 10px',
      borderBottom: `1px solid ${L.lineSoft}`,
      flexWrap: 'wrap',
      background: L.surface,
      position: 'sticky',
      top: 46,
      zIndex: 5,
    }}>
      <ToolBtn title="Жирный" active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()}>
        <BoldOutlined />
      </ToolBtn>
      <ToolBtn title="Курсив" active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()}>
        <ItalicOutlined />
      </ToolBtn>
      <ToolBtn title="Зачёркнутый" active={editor.isActive('strike')} onClick={() => editor.chain().focus().toggleStrike().run()}>
        <StrikethroughOutlined />
      </ToolBtn>
      <ToolSep />
      <ToolBtn title="Подзаголовок" active={editor.isActive('heading', { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>
        H2
      </ToolBtn>
      <ToolBtn title="Подзаголовок 3" active={editor.isActive('heading', { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}>
        H3
      </ToolBtn>
      <ToolSep />
      <ToolBtn title="Список" active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()}>
        <UnorderedListOutlined />
      </ToolBtn>
      <ToolBtn title="Нумерованный список" active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()}>
        <OrderedListOutlined />
      </ToolBtn>
      <ToolBtn title="Цитата" active={editor.isActive('blockquote')} onClick={() => editor.chain().focus().toggleBlockquote().run()}>
        ❝
      </ToolBtn>
      <ToolSep />
      <ToolBtn title="Ссылка" active={editor.isActive('link')} onClick={setLink}>
        <LinkOutlined />
      </ToolBtn>
      <ToolBtn title="Фото (или перетащите файл в текст)" onClick={onInsertImage}>
        <PictureOutlined />
      </ToolBtn>
      <ToolBtn title="Разделитель" onClick={() => editor.chain().focus().setHorizontalRule().run()}>
        <MinusOutlined />
      </ToolBtn>
      <ToolSep />
      <ToolBtn title="Отменить" onClick={() => editor.chain().focus().undo().run()}>
        <UndoOutlined />
      </ToolBtn>
      <ToolBtn title="Повторить" onClick={() => editor.chain().focus().redo().run()}>
        <RedoOutlined />
      </ToolBtn>
      <span style={{ flex: 1 }} />
      <span className="num" style={{ fontSize: 10.5, color: L.faint }}>{words} слов</span>
    </div>
  );
};

/* ─────────────── page ─────────────── */

const NewsEditor: React.FC = () => {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lang, setLang] = useState<Lang>('ru');
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [uploadingCover, setUploadingCover] = useState(false);

  const [form, setForm] = useState<NewsCreate>({
    title_kk: '',
    title_ru: '',
    body_kk: '',
    body_ru: '',
    image_url: '',
    category: undefined,
    is_emergency: false,
    target_district: undefined,
  });
  const formRef = useRef(form);
  formRef.current = form;

  const uploadInlineImage = useCallback(async (file: File, editor: Editor | null) => {
    if (!editor) return;
    const hide = message.loading('Загрузка фото…', 0);
    try {
      const { data } = await newsApi.uploadImage(file);
      editor.chain().focus().setImage({ src: data.url }).run();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Не удалось загрузить фото');
    } finally {
      hide();
    }
  }, []);

  const editorProps = {
    handleDrop: (view: any, event: DragEvent, _slice: any, moved: boolean) => {
      const file = event.dataTransfer?.files?.[0];
      if (!moved && file && file.type.startsWith('image/')) {
        event.preventDefault();
        uploadInlineImage(file, view === editorRuRef.current?.view ? editorRuRef.current : editorKkRef.current);
        return true;
      }
      return false;
    },
    handlePaste: (view: any, event: ClipboardEvent) => {
      const file = Array.from(event.clipboardData?.files || []).find((f) => f.type.startsWith('image/'));
      if (file) {
        event.preventDefault();
        uploadInlineImage(file, view === editorRuRef.current?.view ? editorRuRef.current : editorKkRef.current);
        return true;
      }
      return false;
    },
  };

  const editorRu = useEditor({
    extensions: [
      StarterKit,
      Image,
      Link.configure({ openOnClick: false }),
      Placeholder.configure({ placeholder: 'Текст новости на русском…' }),
    ],
    content: '',
    editorProps,
    onUpdate: ({ editor }) => setForm((f) => ({ ...f, body_ru: editor.getHTML() })),
  });

  const editorKk = useEditor({
    extensions: [
      StarterKit,
      Image,
      Link.configure({ openOnClick: false }),
      Placeholder.configure({ placeholder: 'Жаңалық мәтіні қазақша…' }),
    ],
    content: '',
    editorProps,
    onUpdate: ({ editor }) => setForm((f) => ({ ...f, body_kk: editor.getHTML() })),
  });

  const editorRuRef = useRef(editorRu);
  const editorKkRef = useRef(editorKk);
  editorRuRef.current = editorRu;
  editorKkRef.current = editorKk;

  const fileInputRef = useRef<HTMLInputElement>(null);
  const activeEditor = lang === 'ru' ? editorRu : editorKk;

  // Загрузка существующей новости / восстановление черновика
  useEffect(() => {
    const applyData = (data: Partial<NewsCreate>) => {
      setForm((f) => ({ ...f, ...data }));
      editorRu?.commands.setContent(data.body_ru || '');
      editorKk?.commands.setContent(data.body_kk || '');
    };

    if (isEdit) {
      setLoading(true);
      newsApi.get(Number(id)).then(({ data }) => {
        applyData({
          title_kk: data.title_kk,
          title_ru: data.title_ru,
          body_kk: data.body_kk || '',
          body_ru: data.body_ru || '',
          image_url: data.image_url || '',
          category: data.category || undefined,
          is_emergency: data.is_emergency,
          target_district: data.target_district || undefined,
        });
      }).catch(() => {
        message.error('Новость не найдена');
        navigate('/news');
      }).finally(() => setLoading(false));
    } else {
      const draft = localStorage.getItem(DRAFT_KEY(id));
      if (draft) {
        try {
          applyData(JSON.parse(draft));
          message.info('Восстановлен несохранённый черновик');
        } catch { /* повреждённый черновик игнорируем */ }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, editorRu, editorKk]);

  // Автосохранение черновика каждые 5 сек
  useEffect(() => {
    const t = setInterval(() => {
      const f = formRef.current;
      if (f.title_ru || f.title_kk || f.body_ru || f.body_kk) {
        localStorage.setItem(DRAFT_KEY(id), JSON.stringify(f));
        setSavedAt(new Date().toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }));
      }
    }, 5000);
    return () => clearInterval(t);
  }, [id]);

  const handleCoverUpload = async (file: File) => {
    setUploadingCover(true);
    try {
      const { data } = await newsApi.uploadImage(file);
      setForm((f) => ({ ...f, image_url: data.url }));
      message.success('Обложка загружена');
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Не удалось загрузить обложку');
    } finally {
      setUploadingCover(false);
    }
    return false;
  };

  const handleSave = async () => {
    if (!form.title_ru || !form.title_kk) {
      setLang(!form.title_ru ? 'ru' : 'kk');
      return message.warning('Заполните заголовки на обоих языках');
    }
    setSaving(true);
    try {
      if (isEdit) {
        await newsApi.update(Number(id), form);
        message.success('Новость обновлена');
      } else {
        await newsApi.create(form);
        message.success('Новость опубликована');
      }
      localStorage.removeItem(DRAFT_KEY(id));
      navigate('/news');
    } catch (e: any) {
      message.error(e.response?.data?.detail || 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const discardDraft = () => {
    localStorage.removeItem(DRAFT_KEY(id));
    navigate('/news');
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 100 }}><Spin /></div>;
  }

  const ruFilled = !!(form.title_ru && form.body_ru && form.body_ru !== '<p></p>');
  const kkFilled = !!(form.title_kk && form.body_kk && form.body_kk !== '<p></p>');

  const langDot = (filled: boolean) => (
    <span style={{
      width: 5, height: 5, borderRadius: '50%', display: 'inline-block',
      background: filled ? L.ok : L.danger, marginRight: 6, verticalAlign: 1,
    }} />
  );

  return (
    <div>
      {/* скрытый input для вставки фото в текст */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        style={{ display: 'none' }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) uploadInlineImage(f, activeEditor);
          e.target.value = '';
        }}
      />

      {/* строка действий */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <Button size="small" icon={<ArrowLeftOutlined />} onClick={() => navigate('/news')}>
          Новости
        </Button>
        <span style={{ fontSize: 13, fontWeight: 600, color: L.ink }}>
          {isEdit ? `Редактирование №${id}` : 'Новая новость'}
        </span>
        {savedAt && (
          <span style={{
            fontSize: 10.5, color: L.muted, background: 'var(--l-fill)',
            borderRadius: 5, padding: '2px 7px',
          }}>
            черновик сохранён {savedAt}
          </span>
        )}
        <span style={{ flex: 1 }} />

        {/* переключатель языка с индикаторами заполненности */}
        <span style={{ display: 'inline-flex', border: `1px solid ${L.line}`, borderRadius: 7, overflow: 'hidden' }}>
          {(['ru', 'kk'] as Lang[]).map((lg) => (
            <span
              key={lg}
              onClick={() => setLang(lg)}
              style={{
                fontSize: 12, padding: '4px 12px', cursor: 'pointer',
                background: lang === lg ? L.ink : 'transparent',
                color: lang === lg ? '#fff' : L.muted,
              }}
            >
              {langDot(lg === 'ru' ? ruFilled : kkFilled)}
              {lg === 'ru' ? 'Русский' : 'Қазақша'}
            </span>
          ))}
        </span>

        {!isEdit && (
          <Button size="small" icon={<DeleteOutlined />} onClick={discardDraft}>
            Сбросить
          </Button>
        )}
        <Button type="primary" loading={saving} onClick={handleSave}>
          {isEdit ? 'Сохранить' : 'Опубликовать'}
        </Button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: 16, alignItems: 'start' }}>
        {/* редактор */}
        <div style={{ border: `1px solid ${L.line}`, borderRadius: 10, background: L.surface, overflow: 'hidden', boxShadow: L.shadow }}>
          <div style={{ padding: '14px 18px 4px' }}>
            <Input
              placeholder={lang === 'ru' ? 'Заголовок новости' : 'Жаңалық тақырыбы'}
              value={lang === 'ru' ? form.title_ru : form.title_kk}
              onChange={(e) =>
                setForm(lang === 'ru' ? { ...form, title_ru: e.target.value } : { ...form, title_kk: e.target.value })
              }
              variant="borderless"
              style={{ fontSize: 20, fontWeight: 600, letterSpacing: '-0.02em', padding: 0 }}
            />
          </div>
          <div style={{ display: lang === 'ru' ? 'block' : 'none' }}>
            <MenuBar editor={editorRu} onInsertImage={() => fileInputRef.current?.click()} />
            <div style={{ padding: '14px 18px 20px' }}>
              <EditorContent editor={editorRu} />
            </div>
          </div>
          <div style={{ display: lang === 'kk' ? 'block' : 'none' }}>
            <MenuBar editor={editorKk} onInsertImage={() => fileInputRef.current?.click()} />
            <div style={{ padding: '14px 18px 20px' }}>
              <EditorContent editor={editorKk} />
            </div>
          </div>
        </div>

        {/* правая колонка */}
        <div style={{ border: `1px solid ${L.line}`, borderRadius: 10, background: L.surface, overflow: 'hidden', boxShadow: L.shadow }}>
          <div style={{ padding: '10px 16px 12px', borderBottom: `1px solid ${L.lineSoft}` }}>
            <div style={{ fontSize: 11, color: L.muted, marginBottom: 6 }}>Обложка</div>
            <Upload.Dragger
              accept="image/jpeg,image/png,image/webp"
              showUploadList={false}
              beforeUpload={handleCoverUpload}
              disabled={uploadingCover}
              style={{ padding: 0 }}
            >
              {form.image_url ? (
                <img
                  src={form.image_url}
                  alt="Обложка"
                  style={{ width: '100%', maxHeight: 140, objectFit: 'cover', borderRadius: 6, display: 'block' }}
                />
              ) : (
                <div style={{ padding: '10px 8px', fontSize: 11.5, color: L.muted }}>
                  <InboxOutlined style={{ fontSize: 20, color: L.faint, display: 'block', marginBottom: 6 }} />
                  {uploadingCover ? 'Загрузка…' : <>Перетащите фото или <span style={{ color: L.accent }}>выберите файл</span></>}
                </div>
              )}
            </Upload.Dragger>
            {form.image_url && (
              <Button
                size="small"
                type="text"
                danger
                style={{ marginTop: 6, fontSize: 11 }}
                onClick={() => setForm({ ...form, image_url: '' })}
              >
                Убрать обложку
              </Button>
            )}
          </div>

          <div style={{ padding: '10px 16px 12px', borderBottom: `1px solid ${L.lineSoft}` }}>
            <div style={{ fontSize: 11, color: L.muted, marginBottom: 6 }}>Категория</div>
            <Select
              placeholder="Выберите категорию"
              value={form.category}
              onChange={(v) => setForm({ ...form, category: v })}
              options={NEWS_CATEGORIES.map((c) => ({ value: c.value, label: c.label }))}
              style={{ width: '100%' }}
              allowClear
            />
          </div>

          <div style={{ padding: '10px 16px 12px', borderBottom: `1px solid ${L.lineSoft}` }}>
            <div style={{ fontSize: 11, color: L.muted, marginBottom: 6 }}>Район (таргетинг push)</div>
            <DistrictSelect
              placeholder="Все жители"
              value={form.target_district}
              onChange={(v) => setForm({ ...form, target_district: v })}
              style={{ width: '100%' }}
            />
          </div>

          <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
            <Switch
              size="small"
              checked={form.is_emergency}
              onChange={(v) => setForm({ ...form, is_emergency: v })}
            />
            <span style={{ fontSize: 12.5, color: form.is_emergency ? L.danger : L.text }}>
              Экстренная — push всем сразу
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewsEditor;
